import { useState } from 'react'

export interface ChangePasswordRequest {
  currentPassword: string
  newPassword: string
  confirmPassword: string
}

export interface ChangePasswordResponse {
  message: string
}

export interface UseChangePasswordResult {
  changePassword: (data: ChangePasswordRequest) => Promise<ChangePasswordResponse | null>
  loading: boolean
  error: string | null
}

export function useChangePassword(): UseChangePasswordResult {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const changePassword = async (data: ChangePasswordRequest): Promise<ChangePasswordResponse | null> => {
    try {
      setLoading(true)
      setError(null)

      // Validate passwords match
      if (data.newPassword !== data.confirmPassword) {
        throw new Error('New passwords do not match')
      }

      // Get auth token
      const token = localStorage.getItem('token')
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('/api/user/change-password', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          currentPassword: data.currentPassword,
          newPassword: data.newPassword
        })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
        throw new Error(errorData.error || `Failed to change password (${response.status})`)
      }

      const responseData: ChangePasswordResponse = await response.json()
      return responseData
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to change password'
      setError(errorMessage)
      console.error('Error changing password:', err)
      return null
    } finally {
      setLoading(false)
    }
  }

  return {
    changePassword,
    loading,
    error
  }
}
